import { ApolloError, useMutation, useQuery } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";

import { ConfirmDialog } from "@/components/molecules/ConfirmDialog";
import {
  CallPlanInfoFragment,
  DeleteCallPlanMutation,
  DeleteCallPlanMutationVariables,
  GetCallPlanDetailQuery,
  GetCallPlanDetailQueryVariables,
} from "@/graphql/graphql";
import { secondsToHMS, transformDateUTC } from "@/utils/date-utils";

import { StatusChip } from "../../Playlists/components/StatusChip";
import { type CallPlanEditOrDeleteState } from "../columns";
import { DELETE_CALL_PLAN_MUTATION } from "../CreateCallPlanModal/CreateCallPlanModal.mutations";
import {
  modalCloseGlyphSx,
  modalCloseIconButtonSx,
  modalContainerSx,
  modalHeaderSx,
  modalTitleSx,
} from "../CreateCallPlanModal/CreateCallPlanModal.styles";
import {
  formatGqlExecutionErrors,
  graphqlClientErrorMessage,
} from "../CreateCallPlanModal/CreateCallPlanModal.utils";
import {
  type ActivityInteractionDetail,
  type CallPlanDetailInteraction,
  InteractionSection,
} from "./InteractionSection";
import { GET_CALL_PLAN_DETAIL_QUERY } from "./ViewCallPlanModal.queries";
import {
  viewCallPlanHeaderActionsSx,
  viewCallPlanHeaderDeleteButtonSx,
  viewCallPlanHeaderEditButtonSx,
  viewCallPlanMetaCaptionSx,
  viewCallPlanMetaRowSx,
  viewCallPlanMetaStackFieldSx,
  viewCallPlanMetaStackRoundSx,
  viewCallPlanMetaStackWeekSx,
  viewCallPlanMetaValueCellSx,
  viewCallPlanModalBodySx,
  viewCallPlanScrollContentSx,
  viewCallPlanScrollSpinnerWrapSx,
  viewCallPlanScrollViewportSx,
} from "./ViewCallPlanModal.styles";

type ViewCallPlanModalProps = {
  callPlanToView?: CallPlanInfoFragment;
  editState?: CallPlanEditOrDeleteState;
  deleteState?: CallPlanEditOrDeleteState;
  onClose: () => void;
  onRequestEdit: (callPlan: CallPlanInfoFragment) => void;
};

const estimateActivityInteractionSeconds = (interaction: ActivityInteractionDetail): number => {
  let sec = 0;

  const audioSeconds = interaction.voiceMessage?.audioFile?.durationInSeconds;
  if (interaction.estimatedDuration && interaction.estimatedDuration > 0) {
    sec += interaction.estimatedDuration;
  } else if (audioSeconds) {
    sec += audioSeconds;
  }

  sec += interaction.contents.reduce((sum, c) => {
    if (c.__typename === "QuestionActivityContent") {
      return sum + (c.silenceBeforeResponseSec ?? 0) + (c.responseTimeLimitSec ?? 0);
    }

    return sum;
  }, 0);

  return Math.round(sec);
};

const estimateCallPlanDetailSeconds = (
  interactions?: CallPlanDetailInteraction[] | null
): number => {
  if (!interactions?.length) {
    return 0;
  }

  return interactions.reduce((total, interaction) => {
    if (interaction.__typename === "ActivityInteraction") {
      return total + estimateActivityInteractionSeconds(interaction);
    }

    return total;
  }, 0);
};

const ViewCallPlanModalHeader: React.FC<
  ViewCallPlanModalProps & {
    deleteInProgress: boolean;
    onRequestDelete: () => void;
  }
> = ({
  callPlanToView,
  onClose,
  editState,
  deleteState,
  deleteInProgress,
  onRequestDelete,
  onRequestEdit,
}) => (
  <Box sx={modalHeaderSx}>
    <Typography variant="h4" sx={modalTitleSx}>
      Call Plan
    </Typography>
    <Box sx={viewCallPlanHeaderActionsSx}>
      <Tooltip title={editState?.disabledTooltip}>
        <span>
          <Button
            disableElevation
            disabled={editState?.disabled}
            sx={viewCallPlanHeaderEditButtonSx(!editState?.disabled)}
            onClick={() => {
              if (callPlanToView && !editState?.disabled) {
                onRequestEdit(callPlanToView);
              }
            }}
          >
            Edit call plan
          </Button>
        </span>
      </Tooltip>
      <Tooltip title={deleteState?.disabledTooltip}>
        <span>
          <Button
            disableElevation
            disabled={deleteState?.disabled || deleteInProgress}
            sx={viewCallPlanHeaderDeleteButtonSx(!deleteState?.disabled)}
            onClick={() => {
              if (callPlanToView && !deleteState?.disabled && !deleteInProgress) {
                onRequestDelete();
              }
            }}
          >
            {deleteInProgress ? <CircularProgress size={18} color="inherit" /> : "Delete call plan"}
          </Button>
        </span>
      </Tooltip>
      <IconButton onClick={onClose} aria-label="Close" size="small" sx={modalCloseIconButtonSx}>
        <CloseIcon sx={modalCloseGlyphSx} />
      </IconButton>
    </Box>
  </Box>
);

const ViewCallPlanMetaSection: React.FC<{
  callPlan: CallPlanInfoFragment;
  estimatedLengthSeconds: number;
}> = ({ callPlan, estimatedLengthSeconds }) => (
  <Box sx={viewCallPlanMetaRowSx}>
    <Stack spacing={0} sx={viewCallPlanMetaStackRoundSx}>
      <Typography sx={viewCallPlanMetaCaptionSx} component="span">
        Round
      </Typography>
      <Box sx={viewCallPlanMetaValueCellSx}>
        <Typography variant="body1">{callPlan.program.round}</Typography>
      </Box>
    </Stack>
    <Stack spacing={0} sx={viewCallPlanMetaStackWeekSx}>
      <Typography sx={viewCallPlanMetaCaptionSx} component="span">
        Week
      </Typography>
      <Box sx={viewCallPlanMetaValueCellSx}>
        <Typography variant="body1">{callPlan.programWeek}</Typography>
      </Box>
    </Stack>
    <Stack spacing={0} sx={viewCallPlanMetaStackFieldSx}>
      <Typography sx={viewCallPlanMetaCaptionSx} component="span">
        Start date
      </Typography>
      <Box sx={viewCallPlanMetaValueCellSx}>
        <Typography variant="body1">{transformDateUTC(callPlan.startDate)}</Typography>
      </Box>
    </Stack>
    <Stack spacing={0} sx={viewCallPlanMetaStackFieldSx}>
      <Typography sx={viewCallPlanMetaCaptionSx} component="span">
        End date
      </Typography>
      <Box sx={viewCallPlanMetaValueCellSx}>
        <Typography variant="body1">
          {callPlan.endDate ? transformDateUTC(callPlan.endDate.toString()) : "Active"}
        </Typography>
      </Box>
    </Stack>
    <Stack spacing={0} sx={viewCallPlanMetaStackFieldSx}>
      <Typography sx={viewCallPlanMetaCaptionSx} component="span">
        Estimated length
      </Typography>
      <Box sx={viewCallPlanMetaValueCellSx}>
        <Typography variant="body1">
          {estimatedLengthSeconds > 0 ? secondsToHMS(estimatedLengthSeconds) : "-"}
        </Typography>
      </Box>
    </Stack>
    <Stack spacing={0} sx={viewCallPlanMetaStackFieldSx}>
      <Typography sx={viewCallPlanMetaCaptionSx} component="span">
        Status
      </Typography>
      <Box sx={viewCallPlanMetaValueCellSx}>
        <StatusChip isActive={callPlan.isActive} />
      </Box>
    </Stack>
  </Box>
);

const ViewCallPlanBody: React.FC<{
  loading: boolean;
  error?: ApolloError;
  sortedInteractions: CallPlanDetailInteraction[];
}> = ({ loading, error, sortedInteractions }) => {
  if (loading) {
    return (
      <Box sx={viewCallPlanScrollSpinnerWrapSx}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" variant="body2">
        Could not load this call plan.
      </Typography>
    );
  }

  if (!sortedInteractions.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        This call plan has no interactions yet.
      </Typography>
    );
  }

  return (
    <>
      {sortedInteractions.map((interaction) => (
        <InteractionSection key={interaction.id} interaction={interaction} />
      ))}
    </>
  );
};

const ViewCallPlanModal: React.FC<ViewCallPlanModalProps> = ({
  callPlanToView,
  editState,
  deleteState,
  onClose,
  onRequestEdit,
}) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteErrorOpen, setDeleteErrorOpen] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(null);

  const [deleteCallPlan, { loading: deleteInProgress }] = useMutation<
    DeleteCallPlanMutation,
    DeleteCallPlanMutationVariables
  >(DELETE_CALL_PLAN_MUTATION, { refetchQueries: ["GetSingleUser"] });

  const handleOnClose = useCallback(() => {
    setDeleteErrorOpen(false);
    setDeleteErrorMessage(null);
    setDeleteConfirmOpen(false);
    onClose();
  }, [onClose]);

  const reportDeleteError = (message: string) => {
    setDeleteErrorMessage(message);
    setDeleteErrorOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!callPlanToView) {
      return;
    }
    setDeleteConfirmOpen(false);
    try {
      const result = await deleteCallPlan({ variables: { id: callPlanToView.id } });
      if (result.data?.deleteCallPlan !== true) {
        reportDeleteError(formatGqlExecutionErrors(result) ?? "Could not delete the call plan.");
        return;
      }
      handleOnClose();
    } catch (e) {
      reportDeleteError(graphqlClientErrorMessage(e, "Could not delete the call plan."));
    }
  };

  const { data, loading, error } = useQuery<
    GetCallPlanDetailQuery,
    GetCallPlanDetailQueryVariables
  >(GET_CALL_PLAN_DETAIL_QUERY, {
    skip: !callPlanToView,
    variables: callPlanToView && { callPlanId: callPlanToView.id },
    fetchPolicy: "cache-and-network",
  });

  const detailedCallPlan = data?.callPlan;
  const callPlan = detailedCallPlan || callPlanToView;

  const sortedInteractions = useMemo(() => {
    const rows = detailedCallPlan?.interactions;
    if (!rows?.length) {
      return [];
    }

    return [...rows].sort((a, b) => a.order - b.order);
  }, [detailedCallPlan?.interactions]);

  const estimatedLengthSeconds = useMemo(
    () => estimateCallPlanDetailSeconds(detailedCallPlan?.interactions),
    [detailedCallPlan?.interactions]
  );

  if (!callPlan) {
    return null;
  }

  return (
    <>
      <Modal open={!!callPlanToView} onClose={handleOnClose}>
        <Box sx={modalContainerSx}>
          <ViewCallPlanModalHeader
            callPlanToView={callPlanToView}
            onClose={handleOnClose}
            editState={editState}
            deleteState={deleteState}
            deleteInProgress={deleteInProgress}
            onRequestDelete={() => setDeleteConfirmOpen(true)}
            onRequestEdit={onRequestEdit}
          />

          <Box sx={viewCallPlanModalBodySx}>
            <ViewCallPlanMetaSection
              callPlan={callPlan}
              estimatedLengthSeconds={estimatedLengthSeconds}
            />

            <Box sx={viewCallPlanScrollViewportSx}>
              <Box sx={viewCallPlanScrollContentSx}>
                <ViewCallPlanBody
                  loading={loading}
                  error={error}
                  sortedInteractions={sortedInteractions}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>

      <ConfirmDialog
        confirmOpen={deleteConfirmOpen}
        dialogContent={
          <Typography variant="body2">Delete this call plan? This cannot be undone.</Typography>
        }
        onClose={() => setDeleteConfirmOpen(false)}
        onSubmit={handleConfirmDelete}
      />

      <Snackbar
        open={deleteErrorOpen}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setDeleteErrorOpen(false)}
        TransitionProps={{ onExited: () => setDeleteErrorMessage(null) }}
      >
        <Alert
          onClose={() => setDeleteErrorOpen(false)}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {deleteErrorMessage ?? "Could not delete the call plan."}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ViewCallPlanModal;
